import { Component, computed, inject, input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import {
  CustomCtaBaseDirective,
  ECustomFieldType,
  ICustomField,
  ICustomValueConfig,
} from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsStatusBadgeComponent } from '@shared/components/analytics-status-badge/analytics-status-badge.component';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { DeliverableAdvancedButtonComponent } from '../../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../../advanced-settings-section/deliverable-advanced-settings-section.component';
import { ETextDeliverableMatchMode, ITextDeliverableConfig } from './text-deliverable.interface';

@Component({
  imports: [
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    MatTooltipModule,
    MatCheckboxModule,
    EditableTitleComponent,
    MatExpansionModule,
    IconComponent,
    MatRadioModule,
    CustomRemoveButtonComponent,
    AnalyticsStatusBadgeComponent,
  ],
  selector: 'app-text-deliverable',
  templateUrl: './text-deliverable.component.html',
})
export class TextDeliverableComponent extends CustomCtaBaseDirective implements OnInit {
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly analyticsBadgeLoading = input<boolean>(false);
  public readonly analyticsKeywordCounts = input<((keyword: string) => number | null) | null>(null);
  public readonly analyticsKeywordJump = input<((keyword: string) => void) | null>(null);
  public readonly analyticsKeywordJumpTargets = input<Record<string, string[]>>({});
  public readonly analyticsKeywordJumpTargetsReady = input<boolean>(true);

  /** Pass config overrides when using this component via composition (not inheritance). */
  public readonly textDeliverableConfig = input<Partial<ITextDeliverableConfig>>({});
  /** Extra parent-level form fields to register for custom-values management (e.g. minInterval, maxInterval). */
  public readonly extraManagedFields = input<ICustomField[]>([]);

  public readonly EAnalyticsStatus = EAnalyticsStatus;
  public config: ITextDeliverableConfig;
  public readonly EDeliverableType = EDeliverableType;
  public readonly ETextDeliverableMatchMode = ETextDeliverableMatchMode;
  public readonly focusedFields = new Set<number>();

  public readonly isTrackingDeliverable = computed(() => {
    return this.deliverableUtilitiesService.isTrackingDeliverable(this.config.type);
  });

  public get formArray(): FormArray<FormGroup> {
    return this.form().get(this.config.arrayFieldName) as FormArray<FormGroup>;
  }

  public get matchMode(): FormControl<ETextDeliverableMatchMode> {
    return this.form().get('matchMode') as FormControl<ETextDeliverableMatchMode>;
  }

  protected getConfigOverrides(): Partial<ITextDeliverableConfig> {
    return {};
  }

  protected get customValueConfig(): ICustomValueConfig {
    return {
      componentFields: {
        getForm: (): FormGroup => this.form(),
        managedFields: [
          { name: 'id', type: ECustomFieldType.string },
          { name: 'matchMode', type: ECustomFieldType.string },
          { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
          ...this.extraManagedFields(),
        ],
      },
      nestedArray: {
        getFormArray: (): FormArray<FormGroup> => this.getFormArray(),
        managedFields: [
          { name: this.config.contentFieldName, type: ECustomFieldType.string },
          { name: 'id', type: ECustomFieldType.string },
        ],
      },
    };
  }

  private readonly deliverableUtilitiesService = inject(DeliverableUtilitiesService);

  private readonly defaultConfig: ITextDeliverableConfig = {
    addButtonTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.titleDeliverable.addKeyword',
    arrayFieldName: 'contents',
    contentFieldName: 'text',
    highlightNamespace: 'titleContent',
    lastActiveCannotBeDeletedTranslationKey:
      'pages.campaignForm.ctaDialog.ctaDetails.deliverables.titleDeliverable.lastActiveTitleCannotBeDeleted',
    placeholderTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.titleDeliverable.placeholder',
    subtitleTranslationKey: 'pages.campaignForm.ctaDialog.ctaDetails.deliverables.titleDeliverable.subtitle',
    type: EDeliverableType.title,
  };

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);
    this.config = { ...this.defaultConfig, ...this.getConfigOverrides() };
  }

  public override ngOnInit(): void {
    // Merge any externally provided config (used when consumed via composition).
    this.config = { ...this.defaultConfig, ...this.getConfigOverrides(), ...this.textDeliverableConfig() };
    super.ngOnInit();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.config.type);
  }

  protected getFormArray(): FormArray<FormGroup> {
    return this.formArray;
  }

  public isNewlyAddedElement(id: string): boolean {
    return FormUtilitiesService.isNewlyHighlightedElement(id, this.config.highlightNamespace);
  }

  public addArrayElement(): void {
    const newForm = new FormGroup({
      [this.config.contentFieldName]: new FormControl('', [Validators.required, Validators.maxLength(512)]),
      customCtaValues: new FormControl<ICustomCtaValue[]>([], { nonNullable: true }),
      exclusiveTo: new FormControl<string | null>(this.isCustomMode() ? this.creatorId()! : null),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(null),
      id: new FormControl<string | null>(uuidv4()),
    });

    this.formArray.push(newForm);
    this.initializeNestedForm(newForm, [{ name: this.config.contentFieldName, type: ECustomFieldType.string }]);
    this.trackNestedArrayItemAdded(this.config.contentFieldName, this.formArray.length - 1);
    this.isChildDeleteDisabled.set(false);
    this.lastActiveChildElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.formArray, this.isCustomMode(), this.creators()),
    );

    FormUtilitiesService.highlightNewElement(newForm, this.config.highlightNamespace);
  }

  public removeArrayElement(index: number): void {
    const form = this.formArray.at(index) as FormGroup;

    const dialogRef = CtaDetailsService.openRemoveConfirmationDialog(
      this.dialog,
      this.translate,
      form,
      this.creators(),
      this.config.type,
    );

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.removeOperations(form, index);
        }
      });

      return;
    }

    this.removeOperations(form, index);
  }

  private removeOperations(form: FormGroup, index: number): void {
    const removedValue = form.get(this.config.contentFieldName)?.value as string | null;

    this.trackNestedArrayItemRemoved(this.config.contentFieldName, index, removedValue);
    this.cleanupNestedForm(form);
    this.formArray.removeAt(index);
    this.checkNestedFormDeleteDisableConditions(this.formArray);
  }

  public onInputFocus(index: number): void {
    this.focusedFields.add(index);
  }

  public onInputBlur(index: number): void {
    this.focusedFields.delete(index);
  }

  public isFieldFocused(index: number): boolean {
    return this.focusedFields.has(index);
  }

  public hasMultipleKeywords(control: FormGroup): boolean {
    const value: string = control.get(this.config.contentFieldName)?.value ?? '';

    return /[^ ;,.|]+[ ;,.|]+[^ ;,.|]/.test(value);
  }

  public hasAnyAnalyticsKeywordJumpTarget(): boolean {
    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    const analyticsKeywordJumpTargets: Record<string, string[]> = this.analyticsKeywordJumpTargets();

    return Object.values(analyticsKeywordJumpTargets).some((keywordTargetIds) => {
      return keywordTargetIds.length > 0;
    });
  }

  public showAnalyticsKeywordJumpLoader(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    if (this.analyticsKeywordJumpTargetsReady()) {
      return false;
    }

    return true;
  }

  public canJumpToAnalyticsKeyword(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    return (this.analyticsKeywordJumpTargets()[keyword] ?? []).length > 0;
  }

  public showAnalyticsKeywordJumpAction(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    if (!this.analyticsKeywordJumpTargetsReady()) {
      return false;
    }

    return this.hasAnyAnalyticsKeywordJumpTarget();
  }

  public disableAnalyticsKeywordJumpAction(keyword: string | null | undefined): boolean {
    if (!this.showAnalyticsKeywordJumpAction(keyword)) {
      return false;
    }

    return !this.canJumpToAnalyticsKeyword(keyword);
  }

  public onAnalyticsKeywordJump(keyword: string | null | undefined): void {
    if (!keyword) {
      return;
    }

    if (!this.canJumpToAnalyticsKeyword(keyword)) {
      return;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return;
    }

    analyticsKeywordJump(keyword);
  }
}
